import React, { useRef, useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import {
  useInviteMembers,
  useRetrieveCurrentPlanzone,
  useUserPreferencesRetrieval,
} from "@planzone/view-models";
import {
  Identifiable,
  InvitedMember,
  inviteMembersError,
  InviteMembersError,
  Role,
} from "@planzone/dto";
import { events } from "@planzone/events";
import { useMessage } from "@components/useMessage";
import { ScreenWatcher } from "@common/screenWatcher";
import { LayoutWatcher } from "@common/layoutWatcher";
import { Button } from "@common/button";
import { Modal } from "@common/modal";
import { TrashIcon } from "@common/trashIcon";
import { Props, FormData } from "./types";
import { Member } from "./member";
import { MessageInput } from "./messageInput";
import styles from "./styles.scss";

const defaultMember: FormData = {
  email: "",
  lastName: "",
  firstName: "",
  language: "fr",
  role: null,
};

function formDataToMember(formData: {
  email: string;
  lastName: string;
  firstName: string;
  language: "fr" | "en";
  role: Identifiable<Role> | null;
}): InvitedMember {
  return formData as InvitedMember;
}

function getInvitationError(
  failedInvitations: { member: InvitedMember; error: InviteMembersError }[],
  { email }: InvitedMember,
) {
  const failure = failedInvitations.find(
    ({ member }) => member.email === email,
  );

  return failure ? failure.error : null;
}

function InviteMembers({ cancel }: Props) {
  const { preferences } = useUserPreferencesRetrieval();
  const { t } = useTranslation();
  const { isRequestPending, failedInvitations, inviteMembers } =
    useInviteMembers();
  const { planzone } = useRetrieveCurrentPlanzone();
  const defaultLang = (preferences.language as "fr" | "en") || "fr";
  const [formData, setFormData] = React.useState<FormData[]>([
    { ...defaultMember, language: defaultLang },
  ]);
  const [message, setMessage] = useState<string>("");

  useMessage(
    (event) =>
      event.type === events.inviteMembersFailed &&
      setFormData(
        formData.map((member) => ({
          ...member,
          status: getInvitationError(event.data.failedInvitations, member)
            ? "error"
            : "success",
        })),
      ),
  );

  return (
    <Modal header={t("inviteMembers.title")} close={cancel}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          inviteMembers({
            members: formData
              .filter((member) => member.status !== "success")
              .map(formDataToMember),
            message,
            planzone,
          });
        }}
        className={styles.inviteMembers}
      >
        <p className={styles.message}>{t("inviteMembers.message")}</p>
        <ScreenWatcher
          renderChildren={({ height }) => (
            <LayoutWatcher
              renderChildren={({ top }) => {
                const maxHeight = height - 250;

                return (
                  <div className={styles.main} style={{ maxHeight }}>
                    <ol className={styles.forms}>
                      {formData.map((member, index) => {
                        const hasFailed = member.status === "error";
                        const hasSucceeded = member.status === "success";

                        return (
                          <li
                            key={index}
                            className={styles.form}
                            style={{ zIndex: formData.length - index }}
                          >
                            <div className={styles.member}>
                              <Member
                                formData={member}
                                hasFailed={hasFailed}
                                hasSucceeded={hasSucceeded}
                                error={
                                  getInvitationError(
                                    failedInvitations,
                                    member,
                                  ) || inviteMembersError
                                }
                                edit={(nextData) =>
                                  setFormData([
                                    ...formData.slice(0, index),
                                    nextData,
                                    ...formData.slice(index + 1),
                                  ])
                                }
                              />
                              <div
                                className={styles.iconBox}
                                onClick={() =>
                                  setFormData([
                                    ...formData.slice(0, index),
                                    ...formData.slice(index + 1),
                                  ])
                                }
                              >
                                <TrashIcon className={styles.icon} />
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                    <p
                      className={styles.addResourceButton}
                      onClick={() =>
                        setFormData([
                          ...formData,
                          { ...defaultMember, language: defaultLang },
                        ])
                      }
                    >
                      + {t("inviteMembers.addMember")}
                    </p>
                    <div className={styles.messageForm}>
                      <div className={styles.group}>
                        <h3 className={styles.label}>
                          {t("inviteMembers.messageTitle")}
                        </h3>
                        <div className={styles.messageInput}>
                          <MessageInput
                            value={message}
                            placeholder={t("inviteMembers.messagePlaceholder", {
                              planzone: planzone.name,
                            })}
                            input={setMessage}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }}
            />
          )}
        />
        <div className={styles.buttons}>
          <Button type="cancel" press={cancel}>
            {t("inviteMembers.cancel")}
          </Button>
          <Button type="submit" status={isRequestPending ? "pending" : "none"}>
            {t("inviteMembers.confirm")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export { InviteMembers };
